import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Mail, Clock, ArrowUpRight, ArrowLeft, Sparkles } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Danke = () => {
  useEffect(() => {
    document.title = "Danke – Moonshine Solutions";
  }, []);

  return (
    <div className="min-h-screen bg-ivory-100 text-ink-900 font-sans relative overflow-hidden">
      {/* Ambient gold glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-gold-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-gold-500/8 blur-3xl" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[400px] rounded-full border border-gold-500/15" />
        <div className="absolute bottom-20 left-1/4 w-[300px] h-[300px] rounded-full border border-ink-700/10" />
      </div>

      {/* Simple top bar */}
      <header className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
        <a
          href="/"
          data-testid="danke-logo-link"
          className="flex items-center gap-2.5 group"
        >
          <div className="relative">
            <div className="w-9 h-9 rounded-full bg-ink-900 flex items-center justify-center">
              <span className="text-gold-500 font-display font-semibold text-sm">M</span>
            </div>
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-gold-500 rounded-full" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-medium text-ink-900 text-[15px] tracking-tight">
              Moonshine
            </div>
            <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-ink-500">
              Solutions
            </div>
          </div>
        </a>

        <a
          href="/"
          data-testid="danke-back-link"
          className="hidden sm:inline-flex items-center gap-2 text-sm text-ink-500 hover:text-gold-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Zur Startseite
        </a>
      </header>

      {/* Main */}
      <main className="relative z-10 max-w-3xl mx-auto px-6 lg:px-12 pt-8 pb-24 lg:pb-32">
        {/* Success mark */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto mb-10 relative w-24 h-24 lg:w-28 lg:h-28"
          data-testid="danke-success-icon"
        >
          <div className="absolute inset-0 rounded-full bg-gold-500/25 blur-2xl" />
          <div className="relative w-full h-full rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center shadow-2xl ring-4 ring-ivory-100">
            <Check className="w-12 h-12 lg:w-14 lg:h-14 text-ink-900" strokeWidth={3} />
          </div>
          {/* Sparkles decoration */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0"
          >
            <Sparkles className="absolute -top-2 -right-2 w-5 h-5 text-gold-600" />
            <Sparkles className="absolute -bottom-2 -left-2 w-4 h-4 text-gold-500" />
          </motion.div>
        </motion.div>

        {/* Overline */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={1}
          variants={fadeUp}
          className="text-center text-xs uppercase tracking-[0.28em] font-bold text-gold-700 mb-6"
        >
          Alles erledigt
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial="hidden"
          animate="visible"
          custom={2}
          variants={fadeUp}
          data-testid="danke-headline"
          className="text-center font-display font-medium text-ink-900 text-4xl sm:text-5xl lg:text-[4rem] leading-[1.05] tracking-[-0.03em] mb-8"
        >
          Vielen Dank für dein{" "}
          <span className="italic font-serif-display font-normal text-gold-gradient">
            Vertrauen.
          </span>
        </motion.h1>

        {/* Lead */}
        <motion.p
          initial="hidden"
          animate="visible"
          custom={3}
          variants={fadeUp}
          className="text-center text-lg lg:text-xl text-ink-500 leading-relaxed mb-14 max-w-2xl mx-auto"
        >
          Deine Angaben sind bei mir angekommen. Ich melde mich in den nächsten
          Tagen persönlich bei dir – ganz in Ruhe und ohne Druck.
        </motion.p>

        {/* Info cards */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={4}
          variants={fadeUp}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14"
        >
          <div className="rounded-2xl bg-white border border-ink-700/10 p-6 flex items-start gap-4">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-ink-900 flex items-center justify-center">
              <Mail className="w-5 h-5 text-gold-500" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gold-700 font-semibold mb-1">
                Schritt 1
              </div>
              <div className="font-display text-lg font-medium text-ink-900 leading-snug">
                Deine Analyse per Mail
              </div>
              <p className="text-sm text-ink-500 mt-1 leading-relaxed">
                Du erhältst in Kürze eine erste Einschätzung direkt in dein Postfach.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-ink-700/10 p-6 flex items-start gap-4">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-ink-900 flex items-center justify-center">
              <Clock className="w-5 h-5 text-gold-500" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gold-700 font-semibold mb-1">
                Schritt 2
              </div>
              <div className="font-display text-lg font-medium text-ink-900 leading-snug">
                Persönliches Gespräch
              </div>
              <p className="text-sm text-ink-500 mt-1 leading-relaxed">
                Wir vereinbaren einen Termin und schauen deine Situation gemeinsam an.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Signature block */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={5}
          variants={fadeUp}
          className="text-center"
        >
          <div className="text-ink-500 text-base mb-3">Freundliche Grüsse</div>
          <div
            className="font-serif-display italic text-4xl sm:text-5xl text-ink-900 mb-2 select-none"
            style={{ letterSpacing: "-0.02em" }}
          >
            Pascal Schwab
          </div>
          <div className="text-xs uppercase tracking-[0.22em] text-gold-700 font-semibold">
            Gründer · Moonshine Solutions GmbH
          </div>
        </motion.div>

        {/* Gold divider */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={6}
          variants={fadeUp}
          className="mt-16 mb-14 mx-auto max-w-xs h-px gold-line"
        />

        {/* Bonus CTA to main site */}
        <motion.div
          initial="hidden"
          animate="visible"
          custom={7}
          variants={fadeUp}
          className="relative rounded-3xl bg-gradient-to-br from-ink-800 to-ink-900 px-8 py-10 lg:px-12 lg:py-12 overflow-hidden text-ivory-100 ring-1 ring-gold-500/25"
        >
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-gold-500/20 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-gold-300 mb-4">
              Während du wartest
            </div>
            <h2 className="font-display text-2xl lg:text-3xl leading-tight mb-4">
              Entdecke unsere{" "}
              <span className="italic font-serif-display text-gold-gradient">
                neuen Angebote.
              </span>
            </h2>
            <p className="text-ivory-100/75 text-base leading-relaxed mb-6 max-w-xl">
              Auf unserer Hauptseite findest du weitere Insights, aktuelle Themen
              rund um Vorsorge, Vermögen und Edelmetalle – und was wir sonst noch
              für dich vorbereitet haben.
            </p>
            <a
              href="https://moonshine-solutions.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="danke-website-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-gold-500 hover:bg-gold-400 text-ink-900 px-6 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-2xl"
            >
              Zu moonshine-solutions.com
              <span className="w-7 h-7 rounded-full bg-ink-900 text-gold-300 flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </span>
            </a>
          </div>
        </motion.div>

        {/* Footnote */}
        <motion.p
          initial="hidden"
          animate="visible"
          custom={8}
          variants={fadeUp}
          className="mt-10 text-center text-xs text-ink-500"
        >
          Deine Angaben werden vertraulich behandelt und nur für die persönliche
          Beratung verwendet.
        </motion.p>
      </main>
    </div>
  );
};

export default Danke;
